import type { OrderRule } from "components/Signals/SignalsModels";
import type { TransformFn } from "./Types";

// Unknown values are intentionally pushed to one edge of the result set
// depending on the configured direction.
const UNKNOWN_ORDER = Number.MAX_SAFE_INTEGER;

const normalizeOrderingValue = (value: unknown): string =>
    `${value ?? ""}`.trim().toLowerCase();

const stableSort = <T>(
    items: T[],
    compare: (left: T, right: T) => number,
): T[] =>
    items
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
            const result = compare(left.item, right.item);
            if (result !== 0) return result;
            return left.index - right.index;
        })
        .map(({ item }) => item);

const createNoopTransform = (): TransformFn => (res) => res;

const createSafeTransform = (transform: TransformFn): TransformFn => {
    return (res, ctx) => {
        try {
            return transform(res, ctx);
        } catch {
            // Preserve backend response order when an unknown/malformed rule
            // cannot be applied client-side.
            return res;
        }
    };
};

// Normalizes the flexible `orderByField` config to the actual datum property used by Signals chart
// responses: which axis the field names, and whether it names that axis's identifier or its label.
// The aliases keep endpoint config readable without forcing every config to know the raw response
// shape.
const ORDER_BY_FIELDS = new Map<
    string,
    { axis: "x" | "grouping"; isIdentifier: boolean }
>([
    ["x", { axis: "x", isIdentifier: false }],
    ["xLabel", { axis: "x", isIdentifier: false }],
    ["xIdentifier", { axis: "x", isIdentifier: true }],
    ["grouping", { axis: "grouping", isIdentifier: false }],
    ["groupingLabel", { axis: "grouping", isIdentifier: false }],
    ["series", { axis: "grouping", isIdentifier: false }],
    ["groupingIdentifier", { axis: "grouping", isIdentifier: true }],
    ["seriesIdentifier", { axis: "grouping", isIdentifier: true }],
]);

// A datum's value for an `orderByField`, falling back to the other value on the same axis when the
// named one is absent.
//
// `preferIdentifier` reads the identifier whichever name the rule used, which is what bucket
// ordering needs: a bucket's identifier is built to sort, while its label is built to read.
// "Feb 2026" sorts before "Jan 2026"; "2026-02-01" does not sort before "2026-01-01".
const getFieldValue = (
    datum: any,
    field: string | null,
    preferIdentifier = false,
): string => {
    if (!field) return "";

    const alias = ORDER_BY_FIELDS.get(field);
    if (!alias) {
        const value = datum?.[field];
        return value === undefined || value === null ? "" : `${value}`;
    }

    const label = datum?.[alias.axis];
    const identifier = datum?.[`${alias.axis}_identifier`];

    return `${(preferIdentifier || alias.isIdentifier ? (identifier ?? label) : (label ?? identifier)) ?? ""}`;
};

// For "volume", `null` means sort each row directly by its numeric y value.
// When a field is provided, we aggregate totals by that field first so all rows
// for the same category/series stay together.
const createVolumeTransform = (orderBy: OrderRule): TransformFn => (res) => {
    if (!res.data?.length) return res;

    const { orderByDirection, orderByField } = orderBy;

    if (!orderByField) {
        return {
            ...res,
            data: stableSort(
                res.data,
                (left: any, right: any) =>
                    orderByDirection === "ascending"
                        ? (left?.y ?? 0) - (right?.y ?? 0)
                        : (right?.y ?? 0) - (left?.y ?? 0),
            ),
        };
    }

    const totals = new Map<string, number>();

    res.data.forEach((datum) => {
        const key = normalizeOrderingValue(getFieldValue(datum, orderByField));
        totals.set(key, (totals.get(key) ?? 0) + (datum?.y ?? 0));
    });

    return {
        ...res,
        data: stableSort(res.data, (left: any, right: any) => {
            const leftTotal =
                totals.get(normalizeOrderingValue(getFieldValue(left, orderByField))) ??
                0;
            const rightTotal =
                totals.get(normalizeOrderingValue(getFieldValue(right, orderByField))) ??
                0;

            return orderByDirection === "ascending"
                ? leftTotal - rightTotal
                : rightTotal - leftTotal;
        }),
    };
};

// Alphabetical sorting always compares the configured field text directly.
const createAlphabeticalTransform = (
    orderBy: OrderRule,
): TransformFn => (res) => {
    if (!orderBy.orderByField) return res;

    return {
        ...res,
        data: stableSort(res.data, (left: any, right: any) => {
            const leftValue = getFieldValue(left, orderBy.orderByField);
            const rightValue = getFieldValue(right, orderBy.orderByField);

            return orderBy.orderByDirection === "ascending"
                ? leftValue.localeCompare(rightValue, undefined, {
                      sensitivity: "base",
                  })
                : rightValue.localeCompare(leftValue, undefined, {
                      sensitivity: "base",
                  });
        }),
    };
};

const createQuestionOrderTransform = (
    orderBy: OrderRule,
): TransformFn => (res, ctx) => {
    if (!ctx.questionOrderMap?.size || !orderBy.orderByField) return res;

    return {
        ...res,
        data: stableSort(res.data, (left: any, right: any) => {
            const leftOrder =
                ctx.questionOrderMap?.get(
                    normalizeOrderingValue(
                        getFieldValue(left, orderBy.orderByField),
                    ),
                ) ?? UNKNOWN_ORDER;
            const rightOrder =
                ctx.questionOrderMap?.get(
                    normalizeOrderingValue(
                        getFieldValue(right, orderBy.orderByField),
                    ),
                ) ?? UNKNOWN_ORDER;

            return orderBy.orderByDirection === "ascending"
                ? leftOrder - rightOrder
                : rightOrder - leftOrder;
        }),
    };
};

const createQuestionResponseOrderTransform = (
    orderBy: OrderRule,
): TransformFn => (res, ctx) => {
    if (!ctx.questionResponseOrderMap?.size || !orderBy.orderByField) return res;

    return {
        ...res,
        data: stableSort(res.data, (left: any, right: any) => {
            const leftOrder =
                ctx.questionResponseOrderMap?.get(
                    normalizeOrderingValue(
                        getFieldValue(left, orderBy.orderByField),
                    ),
                ) ?? UNKNOWN_ORDER;
            const rightOrder =
                ctx.questionResponseOrderMap?.get(
                    normalizeOrderingValue(
                        getFieldValue(right, orderBy.orderByField),
                    ),
                ) ?? UNKNOWN_ORDER;

            return orderBy.orderByDirection === "ascending"
                ? leftOrder - rightOrder
                : rightOrder - leftOrder;
        }),
    };
};

/**
 * Orders continuous buckets by their own sequence: chronologically for dates, low to high for
 * numeric ranges. One transform serves both, because both identifier schemes are built so that
 * ordinary string comparison already puts them in order.
 */
const createBucketOrderTransform = (orderBy: OrderRule): TransformFn => (res) => {
    const { orderByField, orderByDirection } = orderBy;
    if (!orderByField || !res.data?.length) return res;

    return {
        ...res,
        data: stableSort(res.data, (left: any, right: any) => {
            const leftValue = getFieldValue(left, orderByField, true).trim();
            const rightValue = getFieldValue(right, orderByField, true).trim();

            // Numeric collation so an unpadded ordinal still orders correctly; ISO dates compare
            // the same either way.
            const comparison = leftValue.localeCompare(rightValue, undefined, {
                numeric: true,
                sensitivity: "base",
            });

            return orderByDirection === "ascending" ? comparison : -comparison;
        }),
    };
};

const createFixedOrderTransform = (
    orderBy: OrderRule & { orderByValues: string[] },
): TransformFn => {
    // Descending uses the array as authored. Ascending reverses the authored
    // order so callers only define a single canonical list.
    const orderedValues =
        orderBy.orderByDirection === "ascending"
            ? [...orderBy.orderByValues].reverse()
            : [...orderBy.orderByValues];
    const orderMap = new Map<string, number>();

    orderedValues.forEach((value, index) => {
        const normalizedValue = normalizeOrderingValue(value);
        if (!normalizedValue.length || orderMap.has(normalizedValue)) return;

        const sortIndex =
            orderBy.orderByDirection === "ascending" ? index + 1 : index;
        orderMap.set(normalizedValue, sortIndex);
    });

    // Ascending puts unknown values first; descending puts them last.
    const unknownOrder =
        orderBy.orderByDirection === "ascending" ? 0 : UNKNOWN_ORDER;

    return (res) => {
        if (!orderBy.orderByField) return res;

        return {
            ...res,
            data: stableSort(res.data, (left: any, right: any) => {
                const leftOrder =
                    orderMap.get(
                        normalizeOrderingValue(
                            getFieldValue(left, orderBy.orderByField),
                        ),
                    ) ?? unknownOrder;
                const rightOrder =
                    orderMap.get(
                        normalizeOrderingValue(
                            getFieldValue(right, orderBy.orderByField),
                        ),
                    ) ?? unknownOrder;

                return leftOrder - rightOrder;
            }),
        };
    };
};

export const resolveOrdering = (
    orderBy: OrderRule | null | undefined,
): TransformFn | undefined => {
    if (!orderBy || typeof orderBy !== "object") return undefined;
    const values = orderBy.orderByValues;

    if (values === "volume") {
        return createSafeTransform(createVolumeTransform(orderBy));
    }

    if (values === "alphabetical") {
        return createSafeTransform(createAlphabeticalTransform(orderBy));
    }

    if (values === "questionOrder") {
        return createSafeTransform(createQuestionOrderTransform(orderBy));
    }

    if (values === "questionResponseOrder") {
        return createSafeTransform(createQuestionResponseOrderTransform(orderBy));
    }

    if (values === "bucketOrder") {
        return createSafeTransform(createBucketOrderTransform(orderBy));
    }

    if (!Array.isArray(values)) {
        return createNoopTransform();
    }

    return createSafeTransform(
        createFixedOrderTransform({
            ...orderBy,
            orderByValues: values.map((value) => `${value ?? ""}`),
        }),
    );
};
